import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { VscSaveAs } from "react-icons/vsc";
import * as Yup from "yup";
import { profileChange, useUser } from "../../redux/services/authSlice";
import { useDispatch } from "react-redux";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ProfilePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [profileLoading, setProfileLoading] = useState(false);
  const initialValues = {
    classesId: user?._id,
    name: "",
    ownerName: "",
    email: "",
    referralCode: "",
    image: "",
    contact: {
      mobile: "",
      countryCode: "",
    },
  };
  const validationSchema = Yup.object({
    name: Yup.string().required("Class name is required"),
    ownerName: Yup.string().required("Owner name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    referralCode: Yup.string().required("Referral code is required"),
    contact: Yup.object({
      countryCode: Yup.string()
        .matches(/^\+\d{1,4}$/, "Invalid country code")
        .required("Country code is required"),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Invalid mobile number")
        .required("Mobile number is required"),
    }),
  });
  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await dispatch(fileUpload(formData));
      if (response) {
        setFieldValue("image", response);
      }
    } catch (error) {
      console.error("File upload failed", error);
      alert("Error uploading file. Please try again.");
    }
  };
  const onSubmit = async () => {
    const filteredValues = { ...values };
    if (filteredValues.image === "") {
      delete filteredValues.image;
    }

    setProfileLoading(true);

    try {
      const response = await dispatch(profileChange(filteredValues));
      if (response) {
        toast(response?.payload?.message);
        navigate("/");
      }
    } catch (error) {
    } finally {
      setProfileLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setFieldValue("image", imageURL);
      handleFileUpload(file);
    }
  };
  useEffect(() => {
    if (user) {
      setValues({
        name: user.name || "",
        ownerName: user.ownerName || "",
        email: user.email || "",
        referralCode: user.referralCode || "",
        image: user.image || "",
        contact: {
          countryCode: user.contact?.countryCode || "",
          mobile: user.contact?.mobile || "",
        },
        classesId: user._id || "",
      });
    }
  }, [user, setValues]);

  return (
    <section className="bg-white rounded-lg border-2 border-slate-300 font-sans duration-300 ease-in-out md:m-4 m-2 p-4">
      <div className="space-y-3">
        <p className="md:text-3xl text-2xl tracking-tight font-medium text-gray-900">
          Update Profile
        </p>
        <p className="md:text-lg text-base tracking-tight font-normal text-gray-700">
          Fill in the information for profile. It will take a couple of minutes.
        </p>
      </div>
      <hr className="my-3" />
      <form
        onSubmit={handleSubmit}
        className="w-full bg-white shadow-sm rounded-xl"
      >
        <div className="mt-3">
          <p className="text-lg font-medium text-gray-700">Image Upload</p>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="block border w-full shadow-sm rounded-lg text-md focus:z-10"
          />
        </div>
        {values.image && (
          <div className="flex h-20 w-20">
            <img
              src={values.image}
              alt="Profile"
              className="w-full h-full mt-3 rounded-md object-cover"
            />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700 mt-4">
          <div>
            <label className="block text-base font-medium text-gray-700">
              Class Name
            </label>
            <input
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className="text-black text-md px-4 py-6 border-2 border-gray-200 h-10 w-full rounded-lg focus:outline-none focus:ring-purple-600 focus:border-purple-600"
            />
            {touched.name && errors.name && (
              <p className="text-red-500 text-sm">{errors.name}</p>
            )}
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className="text-black text-md px-4 py-6 border-2 border-gray-200 h-10 w-full rounded-lg focus:outline-none focus:ring-purple-600 focus:border-purple-600"
              disabled
            />
            {touched.email && errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700 mt-3">
          <div>
            <label className="block text-base font-medium text-gray-700">
              Owner Name
            </label>
            <input
              type="text"
              name="ownerName"
              value={values.ownerName}
              onChange={handleChange}
              onBlur={handleBlur}
              className="text-black text-md px-4 py-6 border-2 border-gray-200 h-10 w-full rounded-lg focus:outline-none focus:ring-purple-600 focus:border-purple-600"
            />
            {touched.ownerName && errors.ownerName && (
              <p className="text-red-500 text-sm">{errors.ownerName}</p>
            )}
          </div>
          <div>
            <label className="block text-base font-medium text-gray-700">
              Referral Code
            </label>
            <input
              type="text"
              name="referralCode"
              value={values.referralCode}
              onChange={handleChange}
              onBlur={handleBlur}
              className="text-black text-md px-4 py-6 border-2 border-gray-200 h-10 w-full rounded-lg focus:outline-none focus:ring-purple-600 focus:border-purple-600"
              disabled
            />
            {touched.referralCode && errors.referralCode && (
              <p className="text-red-500 text-sm">{errors.referralCode}</p>
            )}
          </div>
        </div>

        <div className="mt-3">
          <label className="text-sm font-medium text-gray-700">Contact</label>
          <div className="flex space-x-2">
            <div>
              <input
                type="text"
                name="contact.countryCode"
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                value={values.contact.countryCode}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Country Code"
              />
              {touched.contact?.countryCode && errors.contact?.countryCode && (
                <p className="text-red-500 text-sm">
                  {errors.contact.countryCode}
                </p>
              )}
            </div>
            <div className="w-full">
              <input
                type="text"
                name="contact.mobile"
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                value={values.contact.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Mobile Number"
              />
              {touched.contact?.mobile && errors.contact?.mobile && (
                <p className="text-red-500 text-sm">{errors.contact.mobile}</p>
              )}
            </div>
          </div>
        </div>

        <div className="w-full flex items-center justify-center mt-4">
          <button
            type="submit"
            className="flex items-center justify-center space-x-2 rounded-lg h-[55px] w-[300px] text-md text-white bg-[#124699] hover:bg-opacity-90"
          >
            {profileLoading ? (
              <div className="loader"></div>
            ) : (
              <>
                <VscSaveAs />
                <p className="font-semibold">Update Profile</p>
              </>
            )}
          </button>
        </div>
      </form>
    </section>
  );
};

export default ProfilePage;
